// Template content-form model — pure mapping from a template registry entry
// + the current instance project's content into a display-ready field list.
//
// Plan B2 Task 4 (cogo.template.v1 content-service loop). PURE leaf module:
// no store, no network, no Date.now/Math.random. Two responsibilities:
//
//  1. `bundleFilesFromProjectSource` — reshape whatever the caller's live
//     project state looks like (page-editor's `useEditorStore` OR studio's
//     `currentProject`, both structurally compatible with `ProjectBundleSource`
//     below) into the SAME bundle-relative `files` map shape
//     `@cogo/template`'s `AppBundle` uses ('blocks/<id>/ui.json',
//     'blocks/<id>/data.json', 'data/<name>.json') — the inverse of
//     `features/page-editor/services/template-loader.ts`'s
//     `buildLoadedTemplateFromFiles`. This is what lets `readAtAddress`
//     (Task 1) resolve a project's CURRENT value at a slot's declared
//     address without duplicating resolution logic.
//  2. `buildSlotFormModel` — walk `entry.pkg.slots` and read each slot's
//     current value out of that files map, falling back to the slot's
//     authored `sourceValue` when the address is a ghost (the project has
//     drifted from the template's authored source since instantiation —
//     content-form must still render, not throw).

import type { AppBundle, Json, SlotAddress, SlotConstraint, SlotType } from '@cogo/template'
import { readAtAddress } from '@cogo/template'
import type { TemplateRegistryEntry } from '../registry'

export interface SlotFormField {
  slotId: string
  type: SlotType
  label: string
  currentValue: Json
  constraint?: SlotConstraint
  group?: string
  /**
   * True when this slot can never be honestly persisted by `applySlotValues`
   * in v1 — a `record-list` slot whose address targets a bundle-level
   * `data/<name>.json` static-datasource file rather than a block's own
   * `blocks/<id>/data.json` (see `isUnwritableRecordListAddress`). Instance
   * projects don't carry a template's bundle data files (Plan B1's
   * `createProject` drops `LoadedApp.data`), so there is nowhere server-side
   * to write such a value — the panel renders these fields disabled instead
   * of letting the user believe an edit here will be saved.
   */
  readOnly?: boolean
}

/**
 * Loosely-typed input shape — deliberately NOT `LoadedApp`/`CogoAppDef` so
 * this module stays decoupled from `@cogo/types-v2` and can be fed directly
 * from either `useStudioStore().currentProject` or
 * `useEditorStore.getState()` (`{app, blocks, data}`) without a cast at
 * every call site. Only the fields actually read are declared.
 */
export interface ProjectBundleSource {
  manifest: unknown
  blocks: Record<string, { ui?: unknown; data?: unknown } | null | undefined> | null | undefined
  /** Bundle-level static-datasource records, keyed by datasource NAME (not path) — mirrors `LoadedApp.data`. */
  data?: Record<string, unknown> | null
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Reshapes `source` into `{blocks, appJson}` where `blocks` is the bundle's
 * `files` map WITHOUT the `'app.json'` key (every other bundle-relative
 * path: `blocks/<id>/ui.json`, `blocks/<id>/data.json`,
 * `data/<name>.json`) — the exact contract `buildSlotFormModel` and
 * `applySlotValues` (apply-slot-values.ts) both expect for their own
 * `blocks`/`appJson` parameters. Splitting it this way (rather than
 * returning one `AppBundle['files']` map) mirrors how callers naturally
 * hold `appJson` (`manifest`) and block content separately.
 */
export function bundleFilesFromProjectSource(source: ProjectBundleSource): { blocks: Record<string, Json>; appJson: Json } {
  const blocks: Record<string, Json> = {}
  for (const [blockId, blockDef] of Object.entries(source.blocks ?? {})) {
    if (!blockDef) continue
    if (blockDef.ui !== undefined) blocks[`blocks/${blockId}/ui.json`] = blockDef.ui as Json
    if (blockDef.data !== undefined) blocks[`blocks/${blockId}/data.json`] = blockDef.data as Json
  }

  const manifest = source.manifest
  const datasources = isPlainObject(manifest) ? manifest.datasources : undefined
  if (isPlainObject(datasources)) {
    for (const [name, ds] of Object.entries(datasources)) {
      if (!isPlainObject(ds) || ds.type !== 'static' || typeof ds.path !== 'string') continue
      const key = ds.path.replace(/^\/+/, '')
      const value = (source.data ?? {})?.[name]
      if (value !== undefined) blocks[key] = value as Json
    }
  }

  return { blocks, appJson: (manifest ?? null) as Json }
}

const BLOCK_RECORD_LIST_RE = /^blocks\/([^/]+)\/data\.json$/

/**
 * The owning block id for a record-list address's `file`, or `undefined`
 * when `file` is a bundle-level `data/<name>.json` path with no owning
 * block. Single source of truth for this pattern — shared with
 * `apply-slot-values.ts` so the "which record-list files are block-owned"
 * classification can't drift between the two modules.
 */
export function blockIdForRecordListFile(file: string): string | undefined {
  return BLOCK_RECORD_LIST_RE.exec(file)?.[1]
}

/**
 * True when `address` is a `record-list` address that has no owning block in
 * this project's schema — a bundle-level `data/<name>.json` static-datasource
 * file (`StudioProjectBundleBlock` has no slot for arbitrary bundle-level
 * files). `applySlotValues` fail-closed REJECTS any value targeting one of
 * these (reusing the `slot-address-missing` code — see its comment) instead
 * of silently discarding it into a throwaway clone; `buildSlotFormModel`
 * uses this same predicate to mark the corresponding field `readOnly` so the
 * panel never offers an edit it can't honestly save.
 */
export function isUnwritableRecordListAddress(address: SlotAddress): boolean {
  return address.kind === 'record-list' && blockIdForRecordListFile(address.file) === undefined
}

/** `hero-image-alt` -> `Hero Image Alt`. v1 label heuristic — no separate display-name field exists on `SlotDecl`. */
function humanizeSlotId(slotId: string): string {
  return slotId
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Builds the content-form's field list for `entry`, reading each slot's
 * CURRENT value out of `blocks`/`appJson` (bundle-relative `files`, per
 * `bundleFilesFromProjectSource`'s contract) via `readAtAddress`. A slot
 * whose address doesn't resolve in the current project (drift since
 * instantiation, or a project the panel is rendering before the project's
 * blocks have loaded) falls back to the slot's authored `sourceValue`
 * rather than throwing — the form must always render.
 */
export function buildSlotFormModel(
  entry: TemplateRegistryEntry,
  blocks: Record<string, Json>,
  appJson: Json,
): SlotFormField[] {
  const files: AppBundle['files'] = { ...blocks, 'app.json': appJson }

  return entry.pkg.slots.map((slot) => {
    const addresses = Array.isArray(slot.address) ? slot.address : [slot.address]
    let currentValue: Json = slot.sourceValue
    for (const address of addresses) {
      try {
        currentValue = readAtAddress(files, address)
        break
      } catch {
        // Ghost address — keep the sourceValue fallback and try the next
        // declared address (multi-address slots), if any.
      }
    }
    return {
      slotId: slot.slotId,
      type: slot.type,
      label: humanizeSlotId(slot.slotId),
      currentValue,
      ...(slot.constraint ? { constraint: slot.constraint } : {}),
      ...(slot.group ? { group: slot.group } : {}),
      ...(addresses.some(isUnwritableRecordListAddress) ? { readOnly: true } : {}),
    }
  })
}
